import { useEffect, useState } from "react";

import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { SlArrowDown, SlArrowUp } from "react-icons/sl";
import { useLocation, useNavigate } from "react-router-dom";

import { join } from "@/apis/login";
import {
  OrgOption,
  fetchCommunityOptions,
  fetchGarretOptions,
  fetchLeafOptions,
} from "@/apis/organization";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown/Dropdown";
import Input from "@/components/Input/Input";
import InputLabel from "@/components/Label/InputLabel";
import { LOGIN_MAIN_URL, LOGIN_QR_URL } from "@/constants/routes";
import LoginLayout from "@/pages/Login/components/LoginLayout";
import useLoginStore from "@/store/loginStore";
import useToastStore from "@/store/toastStore";

type JoinCheckParam = {
  userName: string;
  telNo: string;
  cmt: string;
  gar: string;
  leaf: string;
};

const LoginSignUp = () => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      userName: "",
      telNo: "",
      cmt: {
        id: "",
        name: "",
      },
      gar: {
        id: "",
        name: "",
      },
      leaf: {
        id: "",
        name: "",
      },
      isAgree: "off",
    },
  });

  const [communityList, setCommunityList] = useState<OrgOption[]>([]);
  const [garretList, setGarretList] = useState<OrgOption[]>([]);
  const [leafList, setLeafList] = useState<OrgOption[]>([]);
  const [isDetailClick, setIsDetailClick] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const showToast = useToastStore(s => s.showToast);
  // 로그인 직후 navigate state 로 받은 토큰이 없으면(직접 진입 등) 저장된 토큰을 쓴다
  const storedKakaoToken = useLoginStore(s => s.kakaoToken);
  const kakaoToken: string = state || storedKakaoToken;

  useEffect(() => {
    getCommunity();
  }, []);

  //
  const resetList = (type: string) => {
    if (type === "cmt") {
      setCommunityList([]);
      setValue("cmt", {
        id: "",
        name: "",
      });
    } else if (type === "gar") {
      setGarretList([]);
      setValue("gar", {
        id: "",
        name: "",
      });
    } else if (type === "leaf") {
      setLeafList([]);
      setValue("leaf", {
        id: "",
        name: "",
      });
    }
  };

  const cmtId = watch("cmt.id");
  const garId = watch("gar.id");

  // 다락방 리스트 가져오기
  useEffect(() => {
    resetList("gar");
    resetList("leaf");
    if (cmtId !== "") {
      getGarret(cmtId);
    }
  }, [cmtId]);

  // 순 리스트 가져오기
  useEffect(() => {
    resetList("leaf");
    if (cmtId !== "" && garId !== "") {
      getLeaf(cmtId, garId);
    }
  }, [garId]);

  const getCommunity = async () => {
    setCommunityList(await fetchCommunityOptions());
  };

  const getGarret = async (cmtCd: string) => {
    setGarretList(await fetchGarretOptions(cmtCd));
  };

  const getLeaf = async (cmtCd: string, garCd: string) => {
    setLeafList(await fetchLeafOptions(cmtCd, garCd));
  };

  const joinCheck = async (data: JoinCheckParam) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const isJoin = await join({
        userName: data.userName,
        telNo: data.telNo,
        token: kakaoToken,
        provider: "kakao",
        cpsCd: "PTK",
        cmtCd: data.cmt,
        garCd: data.gar,
        leafCd: data.leaf,
      });

      if (isJoin) {
        navigate(LOGIN_QR_URL);
      }
    } catch (error) {
      // 네트워크 오류·5xx 는 axios 인터셉터가 이미 토스트를 띄운다.
      if (!axios.isAxiosError(error) || !error.response) return;
      const status = error.response.status;
      if (status >= 500) return;
      if (status === 409) {
        // 이미 가입된 카카오 계정 → 로그인 화면으로 보내면 useLoginCheck 가 상태에 맞게 안내한다
        showToast(
          "이미 가입된 계정입니다. 로그인 화면으로 이동합니다.",
          "info",
        );
        navigate(LOGIN_MAIN_URL, { replace: true });
        return;
      }
      // 서비스에서 던진 단일 문자열 메시지(예: 소속 정보 오류)는 그대로 보여준다
      const data = error.response.data as { message?: unknown } | undefined;
      const serverMessage =
        typeof data?.message === "string" ? data.message : null;
      showToast(
        status === 400
          ? serverMessage ?? "입력 정보를 다시 확인해주세요."
          : "회원가입에 실패했습니다. 잠시 후 다시 시도해주세요.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginLayout>
      <form
        className="flex h-full w-96 flex-col items-center justify-between py-12 "
        onSubmit={handleSubmit(data => {
          joinCheck({
            ...data,
            cmt: data.cmt.id,
            gar: data.gar.id,
            leaf: data.leaf.id,
          });
        })}
      >
        <div className="mb-4 flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-bold">회원가입</h1>
          <p className="text-base">회원 정보를 입력해주세요.</p>
        </div>
        <div className="flex h-full w-full flex-col gap-4 ">
          <div className="flex w-full flex-col gap-2">
            <InputLabel text="이름" htmlFor="userName" isRequired={true} />
            <Input
              id="userName"
              type="text"
              placeholder="이름을 입력하세요"
              {...register("userName", {
                validate: v => v.trim() !== "" || "이름을 입력해주세요.",
              })}
              variant="default"
            />
            {errors.userName ? (
              <p className="text-xs text-red-500">{errors.userName.message}</p>
            ) : (
              <p className="text-xs text-red-500">
                * 반드시 실명을 입력해주세요.
              </p>
            )}
          </div>
          <div className="flex w-full flex-col gap-2">
            <InputLabel text="전화번호" htmlFor="telNo" isRequired={true} />
            <Input
              id="telNo"
              type="text"
              placeholder="010-0000-0000"
              inputMode="numeric"
              maxLength={13}
              {...register("telNo", {
                validate: v =>
                  /^\d{3}-\d{3,4}-\d{4}$/.test(v) ||
                  "전화번호를 정확히 입력해주세요. (010-0000-0000)",
                onChange: e => {
                  const raw = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 11);
                  let formatted = raw;
                  if (raw.length > 7) {
                    formatted = `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7)}`;
                  } else if (raw.length > 3) {
                    formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
                  }
                  // 제출 실패 후에는 포맷팅된 값으로 즉시 재검증 (붙여넣기 시 에러가 남는 문제 방지)
                  setValue("telNo", formatted, { shouldValidate: isSubmitted });
                },
              })}
              variant="default"
            />
            {errors.telNo && (
              <p className="text-xs text-red-500">{errors.telNo.message}</p>
            )}
          </div>
          <div className="flex w-full flex-col gap-2">
            <InputLabel text="소속" htmlFor="belong" isRequired={true} />
            <div className="grid grid-cols-3 gap-2">
              <Controller
                control={control}
                name="cmt"
                rules={{
                  validate: v => v.id !== "" || "공동체를 선택해주세요.",
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    category="cmt"
                    options={communityList}
                    disabled={false || communityList.length === 0}
                    onChangeOption={onChange}
                    selectedOption={value.name}
                  />
                )}
              />
              <Controller
                control={control}
                name="gar"
                rules={{
                  validate: v => v.id !== "" || "다락방을 선택해주세요.",
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    category="gar"
                    options={garretList}
                    disabled={false || garretList.length === 0}
                    onChangeOption={onChange}
                    selectedOption={value.name}
                  />
                )}
              />
              <Controller
                control={control}
                name="leaf"
                rules={{
                  validate: v => v.id !== "" || "순을 선택해주세요.",
                }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    category="leaf"
                    options={leafList}
                    disabled={false || leafList.length === 0}
                    onChangeOption={onChange}
                    selectedOption={value.name}
                  />
                )}
              />
            </div>
            {(errors.cmt || errors.gar || errors.leaf) && (
              <p className="text-xs text-red-500">
                {errors.cmt?.message ||
                  errors.gar?.message ||
                  errors.leaf?.message}
              </p>
            )}
          </div>
          <div className="flex w-full flex-col gap-2">
            <div className="flex flex-col items-start justify-start">
              <Controller
                control={control}
                name="isAgree"
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <div className="flex flex-row items-start gap-4 text-small">
                    <input
                      type="checkbox"
                      id="private-check"
                      className="mt-1 scale-150 rounded border-2 border-gray-400 accent-blue-600"
                      checked={value === "on"}
                      disabled={!isDetailClick}
                      onChange={() => onChange(value === "on" ? "off" : "on")}
                    />
                    <div>
                      {isDetailClick ? (
                        <div
                          onClick={() => setIsDetailClick(false)}
                          className="flex cursor-pointer flex-row items-start"
                        >
                          <div>
                            <span className="font-bold text-primary">
                              [개인정보 수집과 이용에 대한 동의]{" "}
                            </span>
                            예약자 확인을 위한 개인정보 수집과 이용에
                            동의합니다.
                          </div>
                          <SlArrowUp
                            size={16}
                            className="ml-2 mt-1 py-0 text-xl text-gray-600"
                          />
                        </div>
                      ) : (
                        <div
                          onClick={() => setIsDetailClick(true)}
                          className="flex cursor-pointer flex-row items-start"
                        >
                          <div>
                            <span className="mb-1 block font-bold text-red-600">
                              [개인정보 수집과 이용에 대한 동의]{" "}
                            </span>
                            예약자 확인을 위한 개인정보 수집과 이용에
                            동의합니다.
                          </div>
                          <SlArrowDown
                            size={16}
                            className="ml-2 mt-1 py-0 text-xl text-gray-600"
                          />
                        </div>
                      )}

                      {isDetailClick && (
                        <>
                          <hr className="my-2 mr-5" />
                          <div className="leading-5">
                            - 수집 목적 : 예약자 확인 및 예약 정보 발송
                          </div>
                          <div className="leading-5">
                            - 수집 항목 : 이름, 휴대폰 번호, 소속, 카카오톡
                            프로필 정보
                          </div>
                          <div className="leading-5">
                            - 보유 및 이용 기간 : 회원 탈퇴 시까지
                          </div>
                          <hr className="my-2 mr-5" />
                        </>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        <Button
          variant="primary"
          type="submit"
          disabled={watch("isAgree") === "off" || submitting}
        >
          {submitting ? "가입 처리 중..." : "회원가입"}
        </Button>
      </form>
    </LoginLayout>
  );
};

export default LoginSignUp;
